/* ==========================================================================
   HYBEC AIR — wishlist
   Client-side wishlist backed by localStorage (product handles). No app,
   no customer account, no backend required — works for guests immediately
   and persists per-browser.

   Public API (window.hyWishlist):
     .list()              -> array of handles
     .has(handle)          -> boolean
     .add(handle)
     .remove(handle)
     .toggle(handle)
     .count()

   Markup contract:
     [data-hy-wishlist-toggle="<handle>"]  — heart button, gets .is-active
       and aria-pressed synced to wishlist state; click toggles.
     [data-hy-wishlist-count]              — filled with the current count.
     [data-hy-wishlist-page]               — wishlist page grid container;
       populated client-side via the Storefront AJAX API (/products/{h}.js).

   Fires a `hy:wishlist:change` CustomEvent on window whenever the list
   changes, detail: { list }.
   ========================================================================== */
(function () {
  'use strict';

  var STORAGE_KEY = 'hybec:wishlist';

  function readList() {
    try {
      var raw = localStorage.getItem(STORAGE_KEY);
      var parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  }

  function writeList(list) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
    } catch (e) {
      /* storage unavailable (private mode / quota) — wishlist just won't persist */
    }
    syncButtons(list);
    syncBadges(list);
    window.dispatchEvent(new CustomEvent('hy:wishlist:change', { detail: { list: list } }));
  }

  function has(handle) {
    return readList().indexOf(handle) !== -1;
  }

  function add(handle) {
    var list = readList();
    if (list.indexOf(handle) === -1) {
      list.push(handle);
      writeList(list);
    }
  }

  function remove(handle) {
    var list = readList().filter(function (h) {
      return h !== handle;
    });
    writeList(list);
  }

  function toggle(handle) {
    if (has(handle)) {
      remove(handle);
    } else {
      add(handle);
    }
  }

  function syncButtons(list) {
    list = list || readList();
    document.querySelectorAll('[data-hy-wishlist-toggle]').forEach(function (btn) {
      var handle = btn.getAttribute('data-hy-wishlist-toggle');
      var active = list.indexOf(handle) !== -1;
      btn.classList.toggle('is-active', active);
      btn.setAttribute('aria-pressed', active ? 'true' : 'false');
      btn.setAttribute('aria-label', active ? 'Remove from wishlist' : 'Add to wishlist');
    });
  }

  function syncBadges(list) {
    list = list || readList();
    var count = list.length;
    document.querySelectorAll('[data-hy-wishlist-count]').forEach(function (el) {
      el.textContent = String(count);
      el.hidden = count === 0;
    });
  }

  function initButtons() {
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-hy-wishlist-toggle]');
      if (!btn) return;
      e.preventDefault();
      toggle(btn.getAttribute('data-hy-wishlist-toggle'));
    });
    syncButtons();
    syncBadges();
  }

  /* ---------- Wishlist page: fetch + render saved products ---------- */
  function renderWishlistPage() {
    var container = document.querySelector('[data-hy-wishlist-page]');
    if (!container) return;

    var emptyState = document.querySelector('[data-hy-wishlist-empty]');
    var list = readList();

    if (!list.length) {
      container.innerHTML = '';
      if (emptyState) emptyState.hidden = false;
      return;
    }
    if (emptyState) emptyState.hidden = true;

    Promise.all(
      list.map(function (handle) {
        return fetch('/products/' + handle + '.js')
          .then(function (res) {
            return res.ok ? res.json() : null;
          })
          .catch(function () {
            return null;
          });
      })
    ).then(function (products) {
      container.innerHTML = '';
      var anyFound = false;

      products.forEach(function (product, i) {
        if (!product) {
          /* Product no longer exists / was unpublished — drop it silently. */
          remove(list[i]);
          return;
        }
        anyFound = true;
        container.appendChild(buildCard(product));
      });

      if (!anyFound && emptyState) {
        emptyState.hidden = false;
      }
    });
  }

  function buildCard(product) {
    var img = product.featured_image || (product.images && product.images[0]) || '';
    var price = formatMoney(product.price);

    var card = document.createElement('div');
    card.className = 'hy-product-card';
    card.innerHTML =
      '<a href="' +
      product.url +
      '" class="hy-product-card__link">' +
      '<div class="hy-product-card__img">' +
      (img
        ? '<img src="' + img + '&width=600" alt="' + escapeHtml(product.title) + '" loading="lazy" width="600" height="600">'
        : '') +
      '</div>' +
      '<div class="hy-product-card__body">' +
      '<div class="hy-product-card__name">' +
      escapeHtml(product.title) +
      '</div>' +
      '<div class="hy-product-card__meta"><span>' +
      price +
      '</span></div>' +
      '</div>' +
      '</a>' +
      '<button type="button" class="hy-wishlist-btn is-active" data-hy-wishlist-toggle="' +
      product.handle +
      '" aria-pressed="true" aria-label="Remove from wishlist">' +
      heartSvg() +
      '</button>';
    return card;
  }

  function heartSvg() {
    return (
      '<svg class="hy-wishlist-btn__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" aria-hidden="true">' +
      '<path d="M12 21s-7.5-4.6-10-9.3C.6 8.2 2 4.5 5.6 3.6c2-.5 4 .3 5.2 2 .3.4.9.4 1.2 0 1.2-1.7 3.2-2.5 5.2-2 3.6.9 5 4.6 3.6 8.1-2.5 4.7-10 9.3-10 9.3Z"/>' +
      '</svg>'
    );
  }

  function formatMoney(cents) {
    /* Best-effort formatting when Shopify.formatMoney / theme money format
       isn't available client-side; falls back to a plain decimal. */
    var amount = (cents / 100).toFixed(2);
    if (window.Shopify && window.Shopify.formatMoney && window.theme && window.theme.moneyFormat) {
      try {
        return window.Shopify.formatMoney(cents, window.theme.moneyFormat);
      } catch (e) {
        /* fall through to plain formatting */
      }
    }
    return amount;
  }

  function escapeHtml(str) {
    var div = document.createElement('div');
    div.textContent = str || '';
    return div.innerHTML;
  }

  function init() {
    initButtons();
    renderWishlistPage();
  }

  window.hyWishlist = {
    list: readList,
    has: has,
    add: add,
    remove: remove,
    toggle: toggle,
    count: function () {
      return readList().length;
    },
  };

  window.addEventListener('hy:wishlist:change', function () {
    renderWishlistPage();
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);

  /* Keep in sync across tabs */
  window.addEventListener('storage', function (e) {
    if (e.key === STORAGE_KEY) {
      syncButtons();
      syncBadges();
      renderWishlistPage();
    }
  });
})();
